// @ts-check
import { COMPLEX_SCHEMAS, PRIMITIVE_SCHEMAS } from 'utils/dtdl';
import { formatValueWithUnit, limitDecimals } from './utils';
import { conversion, metricImperialMapping, units } from './units';
import _ from 'lodash';

/**
 * @typedef {object} SchemaUnitType
 * @property {string} unit
 * @property {string} schema
 * @property {function} [render]
 */

/**
 * @typedef {object} ConvertCallbackProps
 * @property {string} value
 * @property {SchemaUnitType} schemaUnit
 * @property {boolean} [showUnit=false]
 */

/**
 * @callback ConvertCallback
 * @param {ConvertCallbackProps} props
 * @returns {string|number}
 */

/**
 * @typedef {object} ConvertArrayCallbackProps
 * @property {[string, string][]|[number, number][]} values
 * @property {SchemaUnitType} schemaUnit
 */

/**
 * @callback ConvertArrayCallback
 * @param {ConvertArrayCallbackProps} props
 * @returns {[values: [string, string][]|[number, number][], schemaUnit: SchemaUnitType]}
 */

/*
 * @param {string} fromUnit
 * @param {string} toUnit
 * @returns {function}
 */
const conversionFuncValue = (fromUnit, toUnit) => {
  const conv = conversion[fromUnit]?.[toUnit];
  const convFunc = _.isFunction(conv) ? conv : (/** @type {number} */value) => conv * value;
  return fromUnit === toUnit ? (/** @type {number} */sameValue) => sameValue : convFunc;
};

/**
 * @param {number} value
 * @param {SchemaUnitType} schema
 * @param {string} fromUnit
 * @param {string} toUnit
 * @param {boolean} showUnit
 * @param {boolean} shouldLimitDecimal
 * @param {string} undefinedValueString
 * @returns {string|number}
 */
const convertValue = (value, schema, fromUnit, toUnit, showUnit, shouldLimitDecimal, undefinedValueString) => {
  switch (schema.schema) {
    case PRIMITIVE_SCHEMAS.double:
    case PRIMITIVE_SCHEMAS.float:
    case PRIMITIVE_SCHEMAS.integer:
    case PRIMITIVE_SCHEMAS.long:
      const limitDecimalFunc = shouldLimitDecimal ? limitDecimals : (val) => val;
      if (fromUnit != null) {
        const newValue = conversionFuncValue(fromUnit, toUnit)(value);
        return !showUnit ? limitDecimalFunc(newValue, undefinedValueString) : formatValueWithUnit(limitDecimalFunc(newValue), units[toUnit], undefinedValueString);
      }
      return limitDecimalFunc(value, undefinedValueString);
    case PRIMITIVE_SCHEMAS.dateTime:
    case PRIMITIVE_SCHEMAS.duration:
    case PRIMITIVE_SCHEMAS.time:
    case PRIMITIVE_SCHEMAS.date:
    case PRIMITIVE_SCHEMAS.string:
      break;
    case PRIMITIVE_SCHEMAS.boolean:
      if (value !== null && value !== undefined) {
        return value.toString();
      }
      return value;
    case COMPLEX_SCHEMAS.Object:
    case COMPLEX_SCHEMAS.Array:
    case COMPLEX_SCHEMAS.Enum:
    case COMPLEX_SCHEMAS.Map:
      return schema.render?.(value) ?? 'Complex Schema not supported';
    default:
      break;
  }
  return value;
};

/**
 * @param {[string,string][]|[number, number][]} values
 * @param {SchemaUnitType} schema
 * @param {string} fromUnit
 * @param {string} system
 * @returns {[string, string][]|[number, number][]}
 */
const convertPairValues = (values, schema, fromUnit, system) => {
  switch (schema.schema) {
    case PRIMITIVE_SCHEMAS.double:
    case PRIMITIVE_SCHEMAS.float:
    case PRIMITIVE_SCHEMAS.integer:
    case PRIMITIVE_SCHEMAS.long:
      if (fromUnit != null) {
        const toUnit = metricImperialMapping[fromUnit]?.[system];
        const convFunc = conversionFuncValue(fromUnit, toUnit);
        return _.map(values, (pair) => [pair[0], convFunc(pair[1])]);
      }
      return values;
    case PRIMITIVE_SCHEMAS.dateTime:
    case PRIMITIVE_SCHEMAS.duration:
    case PRIMITIVE_SCHEMAS.time:
    case PRIMITIVE_SCHEMAS.date:
    case PRIMITIVE_SCHEMAS.string:
      break;
    case PRIMITIVE_SCHEMAS.boolean:
      return _.map(values, (pair) => [pair[0], pair[1].toString()]);
    case COMPLEX_SCHEMAS.Object:
    case COMPLEX_SCHEMAS.Array:
    case COMPLEX_SCHEMAS.Enum:
    case COMPLEX_SCHEMAS.Map:
      return [['', 'Complex Schema not supported']];
    default:
      break;
  }
  return values;
};

/**
 * @param {'metric'|'imperial'} numberSystem
 * @return {{ convertToUserValue: ConvertCallback, convertToSystemValue: ConvertCallback, convertToUserPairArray: ConvertArrayCallback }}
 */
const createConvertFunctions = (numberSystem, shouldLimitDecimal = true, undefinedValueString = '') => {

  const convertToUserValue = ({ value, schemaUnit, showUnit = false }) => {
    const toUnit = metricImperialMapping[schemaUnit.unit]?.[numberSystem];
    return convertValue(value, schemaUnit, schemaUnit.unit, toUnit, showUnit, shouldLimitDecimal, undefinedValueString);
  };
  /**
   * @type {ConvertArrayCallback}
   */
  const convertToUserPairArray = ({ values, schemaUnit }) => {
    const userUnitName = metricImperialMapping[schemaUnit.unit]?.[numberSystem];
    const userUnitValue = units[userUnitName] ?? '';
    return [convertPairValues(values, schemaUnit, schemaUnit.unit, numberSystem), { schema: schemaUnit.schema, unit: userUnitValue }];
  };

  const convertToSystemValue = ({ value, schemaUnit, showUnit = false, shouldLimitDecimal = false, undefinedValueString = '-' }) => {
    const userUnitName = metricImperialMapping[schemaUnit.unit]?.[numberSystem];
    if (userUnitName !== schemaUnit.unit) {
      const toUnit = metricImperialMapping[schemaUnit.unit]?.[numberSystem === 'metric' ? 'imperial' : 'metric'];
      // Reverse the measurement system if property unit does not match the user input system
      return convertValue(value, schemaUnit, userUnitName, toUnit, showUnit, shouldLimitDecimal, undefinedValueString);
    }
    return value;
  };

  return { convertToUserValue, convertToSystemValue, convertToUserPairArray };
};

export default createConvertFunctions;
